import { useContext, useEffect } from "react";
import { Empty } from "antd";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  useBigWinnersConfig,
  useRefreshBigWinners,
  useUpdateBigWinnersConfig,
} from "../../../../queries/casino-configuration/features/useBigWinnersConfig";
import {
  useJackpotConfig,
  useSaveJackpotConfig,
} from "../../../../queries/casino-configuration/features/useJackpotConfig";
import BigWinnersCard from "./components/BigWinnersCard";
import JackpotCard from "./components/JackpotCard";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import "./stylesFeaturesConfiguration.css";

/**
 * Jackpot card + its queries. Kept as a child of FeatureFlagGuard so the
 * jackpot API is not hit when `casino_features.jackpot.enabled` is off
 * (CASENG-1119).
 */
function JackpotFeatureSection() {
  const {
    data: jackpotConfig,
    isLoading: jackpotLoading,
    isError: jackpotError,
    error: jackpotErrorObj,
  } = useJackpotConfig();
  const { mutateAsync: saveJackpotConfig } = useSaveJackpotConfig();

  if (jackpotLoading) {
    return (
      <div className="features-loader" data-testid="jackpot-loader">
        <RebetLoader height="88px" width="88px" />
      </div>
    );
  }
  if (jackpotError) {
    return (
      <div className="features-empty" data-testid="jackpot-error">
        <Empty
          description={
            (jackpotErrorObj as Error)?.message ||
            AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_FETCH_ERROR
          }
        />
      </div>
    );
  }
  return (
    <JackpotCard
      initialConfig={jackpotConfig}
      onSave={(next, previous) => saveJackpotConfig({ next, previous })}
      defaultExpanded
    />
  );
}

/**
 * Casino → Configuration → Features.
 *
 * Hosts a stack of independent feature cards. Each card owns its own enable
 * toggle and tuning form; this page is just the shell + layout so new sibling
 * features slot in by appending another card to the list.
 *
 * Page reachability is permission-gated (`casino.configuration.features`,
 * CASENG-976). Big Winners is GA on this page; Jackpot is gated by
 * `casinoJackpot` (`casino_features.jackpot.enabled`, CASENG-1119).
 */
function FeaturesConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_FEATURES_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  const {
    data: bigWinnersConfig,
    isLoading: bigWinnersLoading,
    isError: bigWinnersError,
    error: bigWinnersErrorObj,
  } = useBigWinnersConfig();
  const { mutateAsync: saveBigWinnersConfig } = useUpdateBigWinnersConfig();
  const { mutateAsync: refreshBigWinners } = useRefreshBigWinners();

  const renderBigWinners = () => {
    if (bigWinnersLoading) {
      return (
        <div className="features-loader" data-testid="features-loader">
          <RebetLoader height="88px" width="88px" />
        </div>
      );
    }
    if (bigWinnersError) {
      return (
        <div className="features-empty" data-testid="big-winners-error">
          <Empty
            description={
              (bigWinnersErrorObj as Error)?.message ||
              AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_FETCH_ERROR
            }
          />
        </div>
      );
    }
    return (
      <BigWinnersCard
        initialConfig={bigWinnersConfig}
        onSave={saveBigWinnersConfig}
        onRefresh={refreshBigWinners}
        defaultExpanded
      />
    );
  };

  return (
    <>
      <div className="features-shell">
        <div className="features-config">
          <div className="features-header">
            <p className="features-subtitle">
              {AppStrings.CASINO_CONFIG_FEATURES_SUBTITLE}
            </p>
          </div>

          <div className="features-list" data-testid="features-list">
            {renderBigWinners()}
            <FeatureFlagGuard mode="conditional" feature="casinoJackpot">
              <JackpotFeatureSection />
            </FeatureFlagGuard>
          </div>
        </div>
      </div>
    </>
  );
}

export default FeaturesConfigurationPage;
